import dynamic from 'next/dynamic'
import { type FC, Fragment } from 'react'

import {
  type OfferV11,
  type PimProductV1Flags,
} from '@redteclab/api/clients/bully'

import { ErrorBoundary } from '../../../error-boundary'
import { type PRODUCT_TEMPLATES } from '../../model/productTemplates'
import { ProductBuyBox } from '../ProductBuyBox'
import { ProductBuyBoxSticky } from '../ProductBuyBox/ProductBuyBoxSticky'
import { ProductOfferBox } from '../ProductBuyBox/ProductSellerBox/ProductOffer/ProductOfferBox'
import { ProductBuyBoxIcons } from '../ProductBuyBoxIcons'
import { useProductPageContext } from '../productPageContext'
import { ProductPrescriptionTypeCollapsible } from '../ProductPrescriptionType'

import { ProductOverviewPrescriptionBonusInfoBanner } from './ProductOverviewPrescriptionBonusInfoBanner'

const ProductRecommendationWidgetOutOfStockSlider = dynamic(async () =>
  import('../ProductRecommendationAreas/ProductRecommendationAreaOutOfStockSlider').then(
    (mod) => mod.ProductRecommendationWidgetOutOfStockSlider,
  ),
)

type ProductOverviewPurchaseSectionProps = {
  bannerAndRecommendationBuyTogether?: FC
  isMobile: boolean
  productAvailableRecommendationWidget?: FC
  shouldRenderPrescriptionTypes: (
    currentOffer: OfferV11 | undefined,
    productTemplate: PRODUCT_TEMPLATES,
    flags: PimProductV1Flags | undefined,
  ) => boolean
  shouldShowStickyBuyBox: boolean
}

export const ProductOverviewPurchaseSection: FC<
  ProductOverviewPurchaseSectionProps
> = ({
  bannerAndRecommendationBuyTogether: BannerAndRecommendationBuyTogether,
  isMobile,
  productAvailableRecommendationWidget: ProductAvailableRecommendationWidget,
  shouldRenderPrescriptionTypes,
  shouldShowStickyBuyBox,
}) => {
  const {
    currentOffer,
    currentPrescriptionType,
    isMasterPage,
    product,
    productTemplate,
  } = useProductPageContext()
  const bonus =
    currentOffer?.prescriptionPriceMap?.[currentPrescriptionType]?.bonus ?? 0
  const shouldShowPrescriptionTypes =
    shouldRenderPrescriptionTypes(
      currentOffer,
      productTemplate,
      product.flags,
    ) && !isMasterPage
  const shouldShowBonusBanner = bonus > 0

  return (
    <div className="tablet:w-1/2 tablet:flex-1 tablet:pl-2.5">
      <Fragment key={product.upid}>
        <ProductBuyBox />

        {shouldShowStickyBuyBox ? (
          <ProductBuyBoxSticky isMobile={isMobile} product={product} />
        ) : null}
      </Fragment>

      <div className="block tablet:hidden">
        <ProductBuyBoxIcons />
      </div>

      <ProductOfferBox />

      {shouldShowPrescriptionTypes ? (
        <ProductPrescriptionTypeCollapsible />
      ) : null}

      {ProductAvailableRecommendationWidget ? (
        <div className="flex w-full flex-1 flex-col gap-5">
          <ProductAvailableRecommendationWidget />
          {BannerAndRecommendationBuyTogether ? (
            <BannerAndRecommendationBuyTogether />
          ) : null}
        </div>
      ) : null}

      <ErrorBoundary>
        <ProductRecommendationWidgetOutOfStockSlider
          className="tablet:hidden"
          itemsPerPage={3}
        />
      </ErrorBoundary>

      {shouldShowBonusBanner ? (
        <ProductOverviewPrescriptionBonusInfoBanner bonus={bonus} />
      ) : null}
    </div>
  )
}
